import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import InputWorkingZipcodeForChargingMap from "../InputComponents/InputZipcode/InputZipcodeForChargingMap";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

import ChargingStationsList from "./ChargingStationsList/ChargingStationsList";

import "./LocationMap.scss";
import { FormattedMessage } from "react-intl";
import useChargingStations from "../../hooks/useChargingStations";
import filterWithinBounds from "../../utils/Helpers/filterWithinBounds";
import BaseGoogleMapReact from "./BaseGoogleMapReact/BaseGoogleMapReact";
import ChargingStationsMapMarker from "./ChargingStationsMapMarker/ChargingStationsMapMarker";
import isSuperChargingStation from "../../utils/predicates/isSuperChargingStation";
import useSelectedStation from "../../hooks/useSelectedStation";
import MapControlPanel from "./MapControlPanel/MapControlPanel";

const ChargingMap = ({ userLocation, isVisible = true, hideList }) => {
  const [bounds, setBounds] = useState({
    nw: undefined,
    ne: undefined,
    sw: undefined,
    se: undefined,
  });
  const [center, setCenter] = useState();
  const [zoom, setZoom] = useState(10);
  const mapRef = useRef();

  useEffect(() => {
    if (!userLocation) return;
    setCenter({
      lat: parseFloat(userLocation.latitude),
      lng: parseFloat(userLocation.longitude),
    });
  }, [userLocation]);

  const filterByStation = (stations, stationType) => {
    switch (stationType) {
      case "All":
        return stations;

      case "High power":
        return stations.filter((station) => isSuperChargingStation(station));

      case "Public":
        return stations.filter(
          (station) =>
            !isSuperChargingStation(station) && station.access_code === "public"
        );

      case "Other":
        return stations.filter(
          (station) =>
            !isSuperChargingStation(station) && station.access_code !== "public"
        );

      default:
        return stations;
    }
  };

  const { chargingStations, fetchChargingStations } = useChargingStations();
  const [selectedStation, selectStation] = useSelectedStation(chargingStations);
  const visibleChargingStations = filterWithinBounds(chargingStations, bounds);

  const [stationType, setStationType] = useState("All");
  let filteredChargingStations = filterByStation(
    visibleChargingStations,
    stationType
  );

  useEffect(() => {
    if (!stationType) return;
  }, [stationType, visibleChargingStations]);

  const onMapChange = ({
    center: newCenter,
    bounds: newBounds,
    zoom: newZoom,
  }) => {
    setBounds(newBounds);
    setZoom(newZoom);
    const { lat: latitude, lng: longitude } = newCenter;
    fetchChargingStations({ latitude, longitude }, newBounds);
  };

  const saveMapRefs = ({ map }) => {
    mapRef.current = map;
  };

  return (
    <div className="ChargingMap">
      <div className="row hide-offscreen">
        <div className="col-sm-12 text-center">
          <h2>
            <FormattedMessage
              id="chargingStations"
              defaultMessage="Charging Stations"
              description="Charging Stations"
            />
          </h2>
        </div>
      </div>

      <InputWorkingZipcodeForChargingMap
        filteredChargingStations={stationType}
        setFilteredChargingStations={setStationType}
      />

      <div className={hideList ? "row charger-background " : "row padding"}>
        <div className="col-sm-9">
          {!userLocation && (
            <div className="spinner-container">
              <LoadingSpinner />
            </div>
          )}
          {userLocation && (
            <div className="map-container">
              {isVisible && (
                <BaseGoogleMapReact
                  center={center}
                  zoom={zoom}
                  onChange={onMapChange}
                  onGoogleApiLoaded={saveMapRefs}
                >
                  {filteredChargingStations.map((point) => (
                    <ChargingStationsMapMarker
                      key={point.id}
                      station={point}
                      lat={parseFloat(point.latitude)}
                      lng={parseFloat(point.longitude)}
                      onMouseOver={() => {
                        selectStation(point.id);
                      }}
                      onClick={() => selectStation(point.id)}
                    />
                  ))}
                </BaseGoogleMapReact>
              )}
              
            </div>
          )}
          <div style={{marginTop: '12px'}}>
            <p className="legal-disclaimer">
              <FormattedMessage
                id="disclaimer.chargingMap"
                defaultMessage="Data from the Alternative Fuel Data Center may not reflect latest availability. Submit suspected missing charging stations {link}. Please confirm availability of charging stations before commencing your journey."
                values={{
                  link: (
                    <a
                      href="https://afdc.energy.gov/stations/#/station/new"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      here
                    </a>
                  ),
                }}
              />
            </p>
          </div>
        </div>

        <div className="col-sm-3">
          <MapControlPanel
            selectedStation={selectedStation}
            chargingStations={filteredChargingStations}
          ></MapControlPanel>
        </div>

        <div className="col-lg-12">
          <ChargingStationsList chargingStations={chargingStations} />
        </div>
      </div>
    </div>
  );
};

export default ChargingMap;

ChargingMap.propTypes = {
  userLocation: PropTypes.object,
  ignoreSuperchargerStations: PropTypes.bool,
};
